import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Timeline,
  Space,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Alert,
  Spin,
  Divider,
  Tooltip,
} from "antd";
import {
  ExperimentOutlined,
  RobotOutlined,
  RocketOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  tabularMLService,
  MLModel,
  PredictionResult,
  ModelPerformance,
  FeatureAnalysis,
} from "../services/tabularMLService";

const { Title, Text } = Typography;
const { Option } = Select;

const MLDashboardContainer = styled.div`
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
`;

const MLCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
`;

const ModelCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  }
`;

const MLDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState<MLModel[]>([]);
  const [predictions, setPredictions] = useState<PredictionResult[]>([]);
  const [featureAnalyses, setFeatureAnalyses] = useState<FeatureAnalysis[]>([]);
  const [selectedModel, setSelectedModel] = useState<MLModel | null>(null);
  const [modelPerformance, setModelPerformance] =
    useState<ModelPerformance | null>(null);
  const [predictionModalVisible, setPredictionModalVisible] = useState(false);
  const [trainingModalVisible, setTrainingModalVisible] = useState(false);
  const [predictionForm] = Form.useForm();
  const [trainingForm] = Form.useForm();

  useEffect(() => {
    const loadData = () => {
      const mlModels = tabularMLService.getModels();
      const recentPredictions = tabularMLService.getPredictions(10);
      const features = tabularMLService.getFeatureAnalyses();

      setModels(mlModels);
      setPredictions(recentPredictions);
      setFeatureAnalyses(features);
      setLoading(false);
    };

    loadData();

    // Update data every 10 seconds
    const interval = setInterval(loadData, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleModelSelect = (model: MLModel) => {
    setSelectedModel(model);
    const performance = tabularMLService.getModelPerformance(model.id);
    setModelPerformance(performance || null);
  };

  const handlePrediction = async (values: Record<string, number | string>) => {
    if (!selectedModel) return;

    try {
      const result = await tabularMLService.predict(selectedModel.id, values);
      setPredictions((prev) => [result, ...prev.slice(0, 9)]);
      setPredictionModalVisible(false);
      predictionForm.resetFields();
    } catch (error) {
      console.error("Prediction failed:", error);
    }
  };

  const handleTrainModel = async (values: any) => {
    try {
      const model = await tabularMLService.trainModel({
        name: values.name,
        type: values.type,
        algorithm: values.algorithm,
        hyperparameters: values.hyperparameters || {},
        trainingData: [],
      });

      setModels((prev) => [...prev, model]);
      setTrainingModalVisible(false);
      trainingForm.resetFields();
    } catch (error) {
      console.error("Training failed:", error);
    }
  };

  const getModelTypeColor = (type: string) => {
    switch (type) {
      case "deep_learning":
        return "purple";
      case "xgboost":
        return "green";
      case "sklearn":
        return "blue";
      case "ensemble":
        return "orange";
      default:
        return "default";
    }
  };

  const getModelStatusIcon = (status: string) => {
    switch (status) {
      case "ready":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "training":
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
      case "error":
        return <WarningOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const modelColumns = [
    {
      title: "Model",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: MLModel) => (
        <Space>
          {getModelStatusIcon(record.status)}
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.algorithm}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={getModelTypeColor(type)}>
          {type.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Accuracy",
      dataIndex: "accuracy",
      key: "accuracy",
      render: (accuracy: number) => (
        <div>
          <Text strong>{(accuracy * 100).toFixed(1)}%</Text>
          <Progress
            percent={accuracy * 100}
            size="small"
            strokeColor={
              accuracy > 0.9
                ? "#52c41a"
                : accuracy > 0.8
                  ? "#faad14"
                  : "#ff4d4f"
            }
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: "F1 Score",
      dataIndex: "f1Score",
      key: "f1Score",
      render: (f1Score: number) => (
        <Text strong>{(f1Score * 100).toFixed(1)}%</Text>
      ),
    },
    {
      title: "Training Time",
      dataIndex: "trainingTime",
      key: "trainingTime",
      render: (time: number) => <Text>{time.toFixed(1)}s</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: MLModel) => (
        <Space>
          <Button
            size="small"
            type="primary"
            onClick={() => handleModelSelect(record)}
          >
            View
          </Button>
          <Button
            size="small"
            onClick={() => {
              setSelectedModel(record);
              setPredictionModalVisible(true);
            }}
          >
            Predict
          </Button>
        </Space>
      ),
    },
  ];

  const predictionColumns = [
    {
      title: "Model",
      dataIndex: "modelId",
      key: "modelId",
      render: (modelId: string) => {
        const model = models.find((m) => m.id === modelId);
        return <Text>{model?.name || modelId}</Text>;
      },
    },
    {
      title: "Prediction",
      dataIndex: "prediction",
      key: "prediction",
      render: (prediction: number | string) => <Text strong>{prediction}</Text>,
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      key: "confidence",
      render: (confidence: number) => (
        <div>
          <Text strong>{(confidence * 100).toFixed(1)}%</Text>
          <Progress
            percent={confidence * 100}
            size="small"
            strokeColor={
              confidence > 0.9
                ? "#52c41a"
                : confidence > 0.8
                  ? "#faad14"
                  : "#ff4d4f"
            }
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: "Processing Time",
      dataIndex: "processingTime",
      key: "processingTime",
      render: (time: number) => <Text>{time.toFixed(2)}s</Text>,
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: Date) => (
        <Text>{timestamp.toLocaleTimeString()}</Text>
      ),
    },
  ];

  if (loading) {
    return (
      <MLDashboardContainer>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "white" }}>Loading ML Models...</p>
        </div>
      </MLDashboardContainer>
    );
  }

  return (
    <MLDashboardContainer>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <MLCard>
            <Space>
              <ExperimentOutlined
                style={{ fontSize: "24px", color: "#667eea" }}
              />
              <div>
                <Title level={2} style={{ margin: 0, color: "#667eea" }}>
                  🤖 Machine Learning Dashboard
                </Title>
                <Text type="secondary">
                  Advanced tabular data analysis with Deep Learning, XGBoost,
                  and Scikit-learn
                </Text>
              </div>
            </Space>
          </MLCard>
        </Col>

        {/* Model Statistics */}
        <Col span={6}>
          <MLCard>
            <Statistic
              title="Total Models"
              value={models.length}
              valueStyle={{ color: "#667eea" }}
              prefix={<ExperimentOutlined />}
            />
          </MLCard>
        </Col>
        <Col span={6}>
          <MLCard>
            <Statistic
              title="Deep Learning"
              value={models.filter((m) => m.type === "deep_learning").length}
              valueStyle={{ color: "#722ed1" }}
              prefix={<RobotOutlined />}
            />
          </MLCard>
        </Col>
        <Col span={6}>
          <MLCard>
            <Statistic
              title="XGBoost"
              value={models.filter((m) => m.type === "xgboost").length}
              valueStyle={{ color: "#52c41a" }}
              prefix={<RocketOutlined />}
            />
          </MLCard>
        </Col>
        <Col span={6}>
          <MLCard>
            <Statistic
              title="Scikit-learn"
              value={models.filter((m) => m.type === "sklearn").length}
              valueStyle={{ color: "#1890ff" }}
              prefix={<BarChartOutlined />}
            />
          </MLCard>
        </Col>

        {/* Model Comparison */}
        <Col span={24}>
          <MLCard
            title={
              <Space>
                <BarChartOutlined style={{ color: "#667eea" }} />
                <span>Model Performance Comparison</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={() => setTrainingModalVisible(true)}
              >
                Train New Model
              </Button>
            }
          >
            <Table
              dataSource={models}
              columns={modelColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </MLCard>
        </Col>

        {/* Selected Model Details */}
        {selectedModel && modelPerformance && (
          <Col span={24}>
            <MLCard
              title={
                <Space>
                  <ExperimentOutlined style={{ color: "#667eea" }} />
                  <span>Model Details: {selectedModel.name}</span>
                </Space>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Statistic
                    title="Accuracy"
                    value={(modelPerformance.metrics.accuracy * 100).toFixed(1)}
                    suffix="%"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Precision"
                    value={(modelPerformance.metrics.precision * 100).toFixed(
                      1,
                    )}
                    suffix="%"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Recall"
                    value={(modelPerformance.metrics.recall * 100).toFixed(1)}
                    suffix="%"
                    valueStyle={{ color: "#faad14" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="F1 Score"
                    value={(modelPerformance.metrics.f1Score * 100).toFixed(1)}
                    suffix="%"
                    valueStyle={{ color: "#722ed1" }}
                  />
                </Col>
              </Row>

              <Divider />

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Title level={5}>Feature Importance</Title>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {Object.entries(selectedModel.featureImportance)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 5)
                      .map(([feature, importance]) => (
                        <div
                          key={feature}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text>{feature}</Text>
                          <Progress
                            percent={importance * 100}
                            size="small"
                            strokeColor="#667eea"
                            showInfo={false}
                            style={{ width: 100 }}
                          />
                          <Text style={{ width: 50, textAlign: "right" }}>
                            {(importance * 100).toFixed(1)}%
                          </Text>
                        </div>
                      ))}
                  </Space>
                </Col>
                <Col span={12}>
                  <Title level={5}>Hyperparameters</Title>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {Object.entries(selectedModel.hyperparameters).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text>{key}:</Text>
                          <Text code>{JSON.stringify(value)}</Text>
                        </div>
                      ),
                    )}
                  </Space>
                </Col>
              </Row>
            </MLCard>
          </Col>
        )}

        {/* Recent Predictions */}
        <Col span={12}>
          <MLCard
            title={
              <Space>
                <LineChartOutlined style={{ color: "#667eea" }} />
                <span>Recent Predictions</span>
              </Space>
            }
          >
            <Table
              dataSource={predictions}
              columns={predictionColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ y: 200 }}
            />
          </MLCard>
        </Col>

        {/* Feature Analysis */}
        <Col span={12}>
          <MLCard
            title={
              <Space>
                <PieChartOutlined style={{ color: "#667eea" }} />
                <span>Feature Analysis</span>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              {featureAnalyses.slice(0, 5).map((feature) => (
                <ModelCard key={feature.featureName} size="small">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text strong>{feature.featureName}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Mean: {feature.statistics.mean?.toFixed(2)} | Std:{" "}
                        {feature.statistics.std?.toFixed(2)}
                      </Text>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Text strong>
                        {(feature.importance * 100).toFixed(1)}%
                      </Text>
                      <br />
                      <Progress
                        percent={feature.importance * 100}
                        size="small"
                        strokeColor="#667eea"
                        showInfo={false}
                      />
                    </div>
                  </div>
                </ModelCard>
              ))}
            </Space>
          </MLCard>
        </Col>
      </Row>

      {/* Prediction Modal */}
      <Modal
        title="Make Prediction"
        open={predictionModalVisible}
        onCancel={() => setPredictionModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={predictionForm}
          onFinish={handlePrediction}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Temperature" name="temperature">
                <Input type="number" placeholder="Enter temperature" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Salinity" name="salinity">
                <Input type="number" placeholder="Enter salinity" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Pressure" name="pressure">
                <Input type="number" placeholder="Enter pressure" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Wind Speed" name="wind_speed">
                <Input type="number" placeholder="Enter wind speed" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Latitude" name="latitude">
                <Input type="number" placeholder="Enter latitude" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Longitude" name="longitude">
                <Input type="number" placeholder="Enter longitude" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlayCircleOutlined />}
            >
              Predict
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Training Modal */}
      <Modal
        title="Train New Model"
        open={trainingModalVisible}
        onCancel={() => setTrainingModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={trainingForm} onFinish={handleTrainModel} layout="vertical">
          <Form.Item
            label="Model Name"
            name="name"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter model name" />
          </Form.Item>
          <Form.Item
            label="Model Type"
            name="type"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select model type">
              <Option value="deep_learning">Deep Learning</Option>
              <Option value="xgboost">XGBoost</Option>
              <Option value="sklearn">Scikit-learn</Option>
              <Option value="ensemble">Ensemble</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Algorithm"
            name="algorithm"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter algorithm name" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlayCircleOutlined />}
            >
              Train Model
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </MLDashboardContainer>
  );
};

export default MLDashboard;
